/**
 * SearchOverlay — Expand-from-icon search surface.
 *
 * v3: One div transitions between a 44px circle (at the header search button)
 * and a full-width card. Content fades in after expansion, out before collapse.
 * Pure CSS transitions on top/right/width/max-height/border-radius.
 */

import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { T } from '@/styles/tokens';
import { SearchMorphIcon, EASE_OUT, SEARCH_TIMING } from '@/components/portfolio/SearchButton';
import { relativeTime } from '@/lib/relativeTime';
import { getArdySearchSuggestions } from '@/lib/ardySearchSuggestions';
import { MEDIUMS } from '@/types/medium';
import type { VerdictSummary } from '@/types';

// Search-specific muted palette (softer than portfolio T tokens)
const S = {
  text: T.text,
  textSec: T.textSecondary,
  textTer: '#C4C4C4',
  tealMuted: '#7ECAC5',
  teal: T.primary,
  red: '#C45A5A',
  card: T.white,
  border: '#E5E5E5',
};

interface SearchOverlayProps {
  isOpen: boolean;
  onClose: () => void;
  summaries: VerdictSummary[];
  floorHourly: number;
  onTapItem: (id: string) => void;
  originY?: number;
  originRight?: number;
}

type Phase = 'closed' | 'entering' | 'open' | 'leaving';

function displayPrice(item: VerdictSummary): string {
  const cents = item.sold && item.soldPrice ? item.soldPrice : (item.gutPrice ?? item.ttcp);
  return `$${Math.round(cents / 100).toLocaleString()}`;
}

function HighlightMatch({ text, query }: { text: string; query: string }) {
  if (!query.trim()) return <>{text}</>;
  const idx = text.toLowerCase().indexOf(query.toLowerCase());
  if (idx === -1) return <>{text}</>;
  return (
    <>
      {text.slice(0, idx)}
      <span style={{ color: S.tealMuted, fontWeight: 700 }}>{text.slice(idx, idx + query.length)}</span>
      {text.slice(idx + query.length)}
    </>
  );
}

const EXPAND_MS = 420;
const COLLAPSE_MS = 380;

export function SearchOverlay({
  isOpen, onClose, summaries, floorHourly, onTapItem, originY, originRight,
}: SearchOverlayProps) {
  const [query, setQuery] = useState('');
  const [phase, setPhase] = useState<Phase>('closed');
  const [surfaceExpanded, setSurfaceExpanded] = useState(false);
  const [contentVisible, setContentVisible] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const timersRef = useRef<ReturnType<typeof setTimeout>[]>([]);

  const suggestions = useMemo(
    () => getArdySearchSuggestions(summaries, floorHourly),
    [summaries, floorHourly],
  );

  const clearTimers = useCallback(() => {
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];
  }, []);

  const sched = useCallback((fn: () => void, ms: number) => {
    timersRef.current.push(setTimeout(fn, ms));
  }, []);

  // Phase state machine — expand from icon choreography
  useEffect(() => {
    if (isOpen && phase === 'closed') {
      clearTimers();
      setQuery('');
      setPhase('entering');
      setSurfaceExpanded(false);
      setContentVisible(false);

      // Double rAF: render collapsed circle first, then expand
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setSurfaceExpanded(true);
          sched(() => setContentVisible(true), 60);
          sched(() => inputRef.current?.focus(), 320);
          sched(() => setPhase('open'), 460);
        });
      });
    }
    if (!isOpen && (phase === 'open' || phase === 'entering')) {
      clearTimers();
      setPhase('leaving');
      setContentVisible(false);
      sched(() => setSurfaceExpanded(false), 120);
      sched(() => setPhase('closed'), 500);
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleClose = useCallback(() => {
    clearTimers();
    setPhase('leaving');
    setContentVisible(false);
    sched(() => setSurfaceExpanded(false), 120);
    sched(() => { setPhase('closed'); onClose(); }, 500);
  }, [onClose, clearTimers, sched]);

  // ESC key handler
  useEffect(() => {
    if (phase === 'open' || phase === 'entering') {
      const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') handleClose(); };
      window.addEventListener('keydown', handler);
      return () => window.removeEventListener('keydown', handler);
    }
  }, [phase, handleClose]);

  // Cleanup on unmount
  useEffect(() => () => clearTimers(), [clearTimers]);

  // Search filter
  const results = useMemo(() => {
    if (!query.trim()) return [];
    const q = query.toLowerCase();
    return summaries.filter(p => {
      const title = (p.title || '').toLowerCase();
      const size = `${p.width}\u00D7${p.height}`;
      const medium = MEDIUMS[p.medium]?.label?.toLowerCase() || '';
      return title.includes(q) || size.includes(q) || medium.includes(q)
        || String(Math.round(p.ttcp / 100)).includes(q);
    });
  }, [query, summaries]);

  if (phase === 'closed') return null;

  const dur = surfaceExpanded ? EXPAND_MS : COLLAPSE_MS;

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={handleClose}
        style={{
          position: 'fixed', inset: 0, zIndex: 49,
          backgroundColor: 'rgba(26,26,26,0.18)',
          backdropFilter: surfaceExpanded ? 'blur(16px)' : 'blur(0px)',
          WebkitBackdropFilter: surfaceExpanded ? 'blur(16px)' : 'blur(0px)',
          opacity: surfaceExpanded ? 1 : 0,
          transition: surfaceExpanded
            ? 'opacity 500ms cubic-bezier(0.25, 0.46, 0.45, 0.94), backdrop-filter 600ms ease, -webkit-backdrop-filter 600ms ease'
            : 'opacity 200ms ease-out, backdrop-filter 200ms ease-out, -webkit-backdrop-filter 200ms ease-out',
        }}
      />

      {/* Surface positioning container — matches header max-width */}
      <div style={{
        position: 'fixed',
        top: 0, left: 0, right: 0,
        maxWidth: 420, margin: '0 auto',
        height: 0,
        zIndex: 50,
        pointerEvents: 'none',
      }}>
        {/* The expanding surface — circle → card */}
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Search portfolio"
          style={{
            position: 'absolute',
            background: S.card,
            overflow: 'hidden',
            pointerEvents: 'auto',
            display: 'flex',
            flexDirection: 'column' as const,

            // Collapsed: at search button position. Expanded: full-width card.
            top: surfaceExpanded
              ? 'calc(env(safe-area-inset-top, 0px) + 4px)'
              : `${originY ?? 100}px`,
            right: surfaceExpanded ? 8 : (originRight ?? 20),
            width: surfaceExpanded ? 'calc(100% - 16px)' : 44,
            maxHeight: surfaceExpanded ? '78vh' : 44,
            borderRadius: surfaceExpanded ? 20 : 22,
            boxShadow: surfaceExpanded
              ? '0 8px 40px rgba(11,12,19,0.14), 0 1px 4px rgba(11,12,19,0.06)'
              : '0 0 0 transparent',

            transition: [
              `top ${dur}ms ${EASE_OUT}`,
              `right ${dur}ms ${EASE_OUT}`,
              `width ${dur}ms ${EASE_OUT}`,
              `max-height ${dur}ms ${EASE_OUT}`,
              `border-radius ${dur}ms ${EASE_OUT}`,
              `box-shadow ${dur}ms ${EASE_OUT}`,
            ].join(', '),
          }}
        >

          {/* Close button — positioned absolute so it stays anchored
              at top-right when the surface collapses to a 44px circle */}
          <button
            onClick={handleClose}
            aria-label="Close search"
            style={{
              position: 'absolute', top: 0, right: 0,
              width: 44, height: 44, borderRadius: 22,
              background: contentVisible ? T.text : 'transparent',
              boxShadow: contentVisible ? '0 2px 12px rgba(11,12,19,0.25)' : 'none',
              border: 'none', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              zIndex: 2,
              transition: `background ${SEARCH_TIMING.iconClose}ms ${EASE_OUT}, box-shadow ${SEARCH_TIMING.iconClose}ms ${EASE_OUT}`,
            }}
          >
            <SearchMorphIcon
              isOpen={contentVisible}
              strokeColor={contentVisible ? '#FFFFFF' : T.textSecondary}
              dur={SEARCH_TIMING.iconClose}
            />
          </button>

          {/* Input Row */}
          <div style={{
            display: 'flex', alignItems: 'center',
            padding: '4px 58px 4px 18px', gap: 8,
            borderBottom: contentVisible ? `1px solid ${S.border}` : '1px solid transparent',
            minHeight: 44,
            transition: `border-color 200ms ${EASE_OUT}`,
          }}>
            <input
              ref={inputRef}
              type="search"
              aria-label="Search portfolio"
              placeholder="Search"
              value={query}
              onChange={e => setQuery(e.target.value)}
              style={{
                flex: 1, fontSize: 16, background: 'transparent',
                border: 'none', outline: 'none', color: S.text,
                fontFamily: T.fontFamily,
                padding: '14px 0',
                caretColor: S.teal,
                letterSpacing: '-0.01em',
                opacity: contentVisible ? 1 : 0,
                transition: `opacity 200ms ${EASE_OUT} ${contentVisible ? '60ms' : '0ms'}`,
                minWidth: 0,
              }}
            />
            {query && contentVisible && (
              <button
                onClick={() => setQuery('')}
                style={{
                  width: 28, height: 28, borderRadius: 14,
                  background: 'rgba(26,26,26,0.03)', border: 'none',
                  cursor: 'pointer', display: 'flex',
                  alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0,
                  animation: 'fadeScaleIn 180ms cubic-bezier(0.34, 1.2, 0.64, 1)',
                }}
              >
                <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                  <path d="M2 2l6 6M8 2l-6 6" stroke={S.textSec} strokeWidth="1.5" strokeLinecap="round" />
                </svg>
              </button>
            )}
          </div>

          {/* Content — fades in after expansion, out before collapse */}
          <div style={{
            flex: 1, overflowY: 'auto', overscrollBehaviorY: 'contain',
            opacity: contentVisible ? 1 : 0,
            transition: `opacity 200ms ${EASE_OUT} ${contentVisible ? '180ms' : '0ms'}`,
          }}>

            {/* Pre-search: Ardy suggestions */}
            {!query.trim() && (
              <div style={{
                padding: '14px 14px 10px',
              }}>
                {suggestions.length > 0 && (
                  <>
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: 5,
                      marginBottom: 10, padding: '0 4px',
                    }}>
                      <span style={{ fontSize: 11, color: S.textTer }}>{'\u2726'}</span>
                      <span style={{
                        fontSize: 10.5, fontWeight: 600, color: S.textTer,
                        textTransform: 'uppercase' as const, letterSpacing: 1,
                        fontFamily: T.fontFamily,
                      }}>
                        Suggestions
                      </span>
                    </div>

                    {/* Suggestion rows — stagger in on open, exit together on close */}
                    {suggestions.map((s, i) => (
                      <div
                        key={s.itemId}
                        onClick={() => { onTapItem(s.itemId); handleClose(); }}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 11,
                          padding: '10px 10px', borderRadius: 12,
                          cursor: 'pointer', marginBottom: 1,
                          opacity: contentVisible ? 1 : 0,
                          transform: contentVisible ? 'translateY(0)' : 'translateY(5px)',
                          transition: contentVisible
                            ? `all 460ms ${EASE_OUT} ${300 + i * SEARCH_TIMING.stagger}ms`
                            : `all 120ms ease-out`,
                        }}
                      >
                        <div style={{
                          width: 34, height: 34, borderRadius: 9,
                          background: 'rgba(26,26,26,0.018)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: 14, color: S.textTer, flexShrink: 0,
                        }}>
                          {s.icon}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{
                            fontSize: 10.5, fontWeight: 500, color: S.textTer,
                            textTransform: 'uppercase' as const, letterSpacing: 0.4, marginBottom: 1,
                            fontFamily: T.fontFamily,
                          }}>{s.label}</div>
                          <div style={{
                            fontSize: 14, fontWeight: 500, color: S.textSec,
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                            fontFamily: T.fontFamily,
                          }}>{s.title}</div>
                        </div>
                        <span style={{
                          fontSize: 12.5, fontWeight: 400, color: S.textTer, flexShrink: 0,
                          fontFamily: T.fontFamily,
                        }}>
                          {s.detail}
                        </span>
                      </div>
                    ))}
                  </>
                )}

              </div>
            )}

            {/* Search Results */}
            {query.trim() && results.length > 0 && (
              <div style={{ padding: '6px 8px' }}>
                <div style={{
                  fontSize: 10.5, fontWeight: 600, color: S.textTer,
                  textTransform: 'uppercase' as const, letterSpacing: 1,
                  padding: '8px 10px 8px',
                  fontFamily: T.fontFamily,
                }}>
                  {results.length} result{results.length !== 1 ? 's' : ''}
                </div>
                {results.map((a, i) => {
                  const title = a.title || `${a.width}\u00D7${a.height}"`;
                  const mediumLabel = MEDIUMS[a.medium]?.label || a.medium;
                  const hourlyDollars = a.effectiveHourly != null
                    ? Math.round(a.effectiveHourly / 100)
                    : null;
                  const aboveFloor = (a.effectiveHourly ?? 0) >= floorHourly;

                  return (
                    <div
                      key={a.id}
                      onClick={() => { onTapItem(a.id); handleClose(); }}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 11,
                        padding: '11px 10px', borderRadius: 12,
                        cursor: 'pointer',
                        animation: `resultSlideIn 400ms cubic-bezier(0.175, 0.885, 0.32, 1) ${i * 35}ms both`,
                      }}
                    >
                      {/* Avatar */}
                      <div style={{
                        width: 38, height: 38, borderRadius: 10,
                        background: `linear-gradient(135deg, rgba(61,171,165,0.07), rgba(61,171,165,0.024))`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontWeight: 700, fontSize: 14, color: S.tealMuted, flexShrink: 0,
                        fontFamily: "'Manrope', 'Satoshi', 'DM Sans', sans-serif",
                      }}>{title.charAt(0).toUpperCase()}</div>

                      {/* Title + metadata */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{
                          fontSize: 14, fontWeight: 600, color: S.text,
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                          fontFamily: T.fontFamily,
                        }}>
                          <HighlightMatch text={title} query={query} />
                        </div>
                        <div style={{
                          fontSize: 12, color: S.textSec, marginTop: 2,
                          fontFamily: T.fontFamily,
                        }}>
                          {a.width}&times;{a.height}" &middot; {mediumLabel} &middot; {relativeTime(a.createdAt)}
                          {a.sold && (
                            <>
                              <span style={{ margin: '0 4px', color: S.textTer }}>&middot;</span>
                              <span style={{ color: S.red, fontSize: 6, verticalAlign: 'middle' }}>{'\u25CF'}</span>
                              <span style={{ color: S.red, fontWeight: 500, marginLeft: 2 }}>Sold</span>
                            </>
                          )}
                        </div>
                      </div>

                      {/* Price + hourly */}
                      <div style={{ textAlign: 'right' as const, flexShrink: 0 }}>
                        <div style={{
                          fontFamily: "'Manrope', 'Satoshi', 'DM Sans', sans-serif",
                          fontWeight: 700, fontSize: 14.5, color: S.text,
                        }}>{displayPrice(a)}</div>
                        {hourlyDollars != null && (
                          <div style={{
                            fontSize: 11.5, fontWeight: 500,
                            color: aboveFloor ? S.tealMuted : S.red,
                            marginTop: 1,
                            fontFamily: T.fontFamily,
                          }}>
                            ${hourlyDollars}/hr {aboveFloor ? '\u2191' : '\u2193'}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* No Results */}
            {query.trim() && results.length === 0 && (
              <div style={{
                display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: 'center',
                padding: '48px 24px 40px', textAlign: 'center' as const,
              }}>
                <div style={{
                  width: 44, height: 44, borderRadius: 12,
                  background: 'rgba(26,26,26,0.016)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  marginBottom: 14,
                }}>
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <circle cx="8.5" cy="8.5" r="6" stroke={S.textTer} strokeWidth="1.6" />
                    <path d="M13 13l4.5 4.5" stroke={S.textTer} strokeWidth="1.6" strokeLinecap="round" />
                    <path d="M6 8.5h5" stroke={S.textTer} strokeWidth="1.3" strokeLinecap="round" />
                  </svg>
                </div>
                <div style={{
                  fontSize: 14, fontWeight: 500, color: S.textSec,
                  fontFamily: T.fontFamily,
                }}>
                  No results for &ldquo;{query}&rdquo;
                </div>
                <div style={{
                  fontSize: 13, color: S.textTer, marginTop: 3,
                  fontFamily: T.fontFamily,
                }}>
                  Try a different title, medium, or size
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes resultSlideIn {
          from { opacity: 0; transform: translateY(5px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes fadeScaleIn {
          from { opacity: 0; transform: scale(0.7); }
          to { opacity: 1; transform: scale(1); }
        }
      `}</style>
    </>
  );
}
